import { type ChangeEvent, type FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Icon } from "@root/shared/components/Icon";
import { SelectBox, type OptionProps } from "@root/shared/components/SelectBox";
import * as styles from "./styles.css";

const YEAR_RANGE = 2;
const MONTHS_IN_YEAR = 12;

type Props = {
  yearName: string;
  monthName: string;
  // Storybook / テストから固定値を注入するため。省略時は現在の年月。
  baseYear?: number;
  baseMonth?: number;
  hasError?: boolean;
};

export const YearMonthSelect: FC<Props> = ({
  yearName,
  monthName,
  baseYear = new Date().getFullYear(),
  baseMonth = new Date().getMonth() + 1,
  hasError,
}) => {
  const { register, watch, setValue } = useFormContext();

  const year = watch(yearName);
  const month = watch(monthName);
  const yearStr = typeof year === "string" ? year : "";
  const monthStr = typeof month === "string" ? month : "";

  const yearOptions = useMemo<readonly OptionProps[]>(
    () => [
      { label: "選択しない", value: "" },
      ...Array.from({ length: YEAR_RANGE + 1 }, (_, i) => {
        const optionYear = baseYear + i;
        return { label: `${optionYear}年`, value: String(optionYear) };
      }),
    ],
    [baseYear],
  );

  // 「現在月〜2 年後の月」を年月ペアで選ばせる仕様。
  // 現在年は baseMonth 以降のみ、最終年 (現在年+2) は baseMonth 以下のみ選択できる。
  const monthOptions = useMemo<readonly OptionProps[]>(() => {
    let start = 1;
    let end = MONTHS_IN_YEAR;
    if (yearStr !== "") {
      const selectedYear = Number(yearStr);
      if (selectedYear === baseYear) start = baseMonth;
      if (selectedYear === baseYear + YEAR_RANGE) end = baseMonth;
    }
    return [
      { label: "選択しない", value: "" },
      ...Array.from({ length: end - start + 1 }, (_, i) => {
        const m = start + i;
        return { label: `${m}月`, value: String(m) };
      }),
    ];
  }, [yearStr, baseYear, baseMonth]);

  const yearRegister = register(yearName, {
    onChange: (e: ChangeEvent<HTMLSelectElement>) => {
      const newYear = e.target.value;
      if (newYear === "" || monthStr === "") return;
      const yearNum = Number(newYear);
      const monthNum = Number(monthStr);
      const min = yearNum === baseYear ? baseMonth : 1;
      const max = yearNum === baseYear + YEAR_RANGE ? baseMonth : MONTHS_IN_YEAR;
      if (monthNum < min || monthNum > max) {
        setValue(monthName, "", { shouldDirty: true });
      }
    },
  });

  return (
    <div className={styles.container}>
      <p className={styles.label}>
        <Icon name="calendar-check" className={styles.icon} fontSize="16px" />
        オープン時期を選択する
      </p>
      <div className={styles.selects}>
        <SelectBox
          className={styles.select}
          selected={!!yearStr}
          hasError={hasError}
          placeholder={`${baseYear}年`}
          isPlaceholderDisabled
          options={yearOptions}
          {...yearRegister}
        />
        <SelectBox
          className={styles.select}
          selected={!!monthStr}
          hasError={hasError}
          placeholder={`${baseMonth}月`}
          isPlaceholderDisabled
          options={monthOptions}
          {...register(monthName)}
        />
      </div>
    </div>
  );
};
